/**
 * RiskHealthDashboard.tsx
 * Shows risk score per company + breakdown and top drivers.
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

function getScoreColor(score: number) {
  if (score < 70) return 'text-destructive';
  if (score < 85) return 'text-yellow-600';
  return 'text-primary';
}

export default function RiskHealthDashboard() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('compute_all_risk_scores');
      if (error) throw error;
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load risk scores');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  const chartData = rows.map((r: any) => ({
    name: r.ingredients?.company_name || 'Unknown',
    score: r.score || 0,
  }));

  return (
    <div className="p-4 space-y-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <AlertTriangle className="text-primary" /> Risk Health
        </h2>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Company Risk Scores</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Bar dataKey="score" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Breakdown & Drivers</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Compliance</TableHead>
                <TableHead>Governance</TableHead>
                <TableHead>Filings</TableHead>
                <TableHead>Overrides</TableHead>
                <TableHead>Top Drivers</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r: any) => (
                <TableRow key={r.company_id}>
                  <TableCell>{r.ingredients?.company_name || 'Unknown'}</TableCell>
                  <TableCell className={getScoreColor(r.score || 0)}>{r.score || 0}</TableCell>
                  <TableCell>{r.subscores?.COMPLIANCE || 0}</TableCell>
                  <TableCell>{r.subscores?.GOVERNANCE || 0}</TableCell>
                  <TableCell>{r.subscores?.FILINGS || 0}</TableCell>
                  <TableCell>{r.subscores?.OVERRIDES || 0}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.ingredients?.late_cnt > 0 && `Late x${r.ingredients.late_cnt} `}
                    {r.ingredients?.due_cnt > 0 && `Due x${r.ingredients.due_cnt} `}
                    {r.ingredients?.filings_failed > 0 &&
                      `Failed filings x${r.ingredients.filings_failed} `}
                    {r.ingredients?.triggers_open > 0 &&
                      `Open triggers x${r.ingredients.triggers_open} `}
                    {r.ingredients?.signal_score > 0 && `Signals ${r.ingredients.signal_score} `}
                    {r.ingredients?.override_sum !== 0 && `Override ${r.ingredients.override_sum}`}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
